// src/components/CampaignList.js
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Snackbar, Alert, TextField, Pagination, Stack, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, FormControl,
  InputLabel, Select, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { deleteCampaign, getAllCampaigns } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDateTimeLocale } from '../utils/datetimeLocal';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormCampaign from './FormCampaign';
import SortableTh from './common/SortableTh';
import { StatusAtivoChip, sxLinhaInativa } from './common/StatusAtivo';
import { useServerSort } from '../hooks/useServerSort';

export default function CampaignList() {
  const [pagina, setPagina] = useState(1);
  const [qtde] = useState(20);

  const [items, setItems] = useState([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);

  const [searchText, setSearchText] = useState('');
  const [termo, setTermo] = useState('');

  const [ativoFiltro, setAtivoFiltro] = useState('');
  const [vigenteFiltro, setVigenteFiltro] = useState('');

  const [confirmId, setConfirmId] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');
  const [form, setForm] = useState({ open: false, id: null });

  const { orderBy, order, requestSort: aplicarOrdem } = useServerSort('vigencia', 'desc');
  const requestSort = (key) => {
    setPagina(1);
    aplicarOrdem(key);
  };

  const carregar = useCallback(async () => {
    try {
      const { data } = await getAllCampaigns({
        indice: pagina, qtde, termo, ativo: ativoFiltro, vigente: vigenteFiltro,
        ordenar: orderBy, direcao: order,
      });
      setItems(data.items ?? []);
      setTotalPaginas(data.totalPages ?? 1);
      setTotalItens(data.totalItems ?? 0);
    } catch (e) {
      setSnackMsg(getApiErrorMessage(e));
      setSnackSeverity('error');
      setSnackOpen(true);
    }
  }, [pagina, qtde, termo, ativoFiltro, vigenteFiltro, orderBy, order]);

  useEffect(() => { carregar(); }, [carregar]);

  const sorted = items;

  const onBuscar = () => { setPagina(1); setTermo(searchText); };
  const onKeyDown = (e) => { if (e.key === 'Enter') onBuscar(); };

  const abrirForm = (id = null) => setForm({ open: true, id });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setSnackMsg(msg);
    setSnackSeverity('success');
    setSnackOpen(true);
    carregar();
  };

  const excluir = async () => {
    try {
      await deleteCampaign(confirmId);
      setSnackMsg('Campanha excluída');
      setSnackSeverity('success');
      setSnackOpen(true);
      setConfirmId(null);
      if (items.length === 1 && pagina > 1) setPagina(pagina - 1);
      else carregar();
    } catch (e) {
      setConfirmId(null);
      setSnackMsg(getApiErrorMessage(e, 'Não foi possível excluir a campanha.'));
      setSnackSeverity('error');
      setSnackOpen(true);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Campanhas promocionais"
        subtitle="Promoções por período aplicadas automaticamente na venda."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirForm()}>
            Nova campanha
          </Button>
        }
      />

      {/* toolbar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Buscar por descrição..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={onKeyDown}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: !!searchText && (
              <IconButton size="small" onClick={() => setSearchText('')}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          }}
          sx={{ flexGrow: 1, minWidth: 240 }}
        />
        <Button variant="outlined" onClick={onBuscar}>Buscar</Button>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={ativoFiltro} label="Status" onChange={(e) => { setPagina(1); setAtivoFiltro(e.target.value); }}>
            <MenuItem value=""><em>Todos</em></MenuItem>
            <MenuItem value="true">Ativas</MenuItem>
            <MenuItem value="false">Inativas</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Vigência agora</InputLabel>
          <Select value={vigenteFiltro} label="Vigência agora" onChange={(e) => { setPagina(1); setVigenteFiltro(e.target.value); }}>
            <MenuItem value=""><em>Indiferente</em></MenuItem>
            <MenuItem value="true">Vigentes</MenuItem>
            <MenuItem value="false">Fora de vigência</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* tabela */}
      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh id="descricao" label="Descrição" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="vigencia" label="Vigência" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="produtos" label="Produtos" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma campanha encontrada
                </TableCell>
              </TableRow>
            ) : sorted.map((c) => (
              <TableRow key={c.id} hover sx={{ cursor: 'pointer', ...sxLinhaInativa(c.ativo) }} onClick={() => abrirForm(c.id)}>
                <TableCell data-label="Descrição">{c.descricao}</TableCell>
                <TableCell data-label="Vigência">
                  {formatDateTimeLocale(c.vigenciaInicio)}
                  {' — '}
                  {formatDateTimeLocale(c.vigenciaFim)}
                </TableCell>
                <TableCell data-label="Status">
                  {c.ativo
                    ? (c.vigenteAgora ? 'Ativa (vigente)' : 'Ativa')
                    : <StatusAtivoChip ativo={false} labelInativo="Inativa" />}
                </TableCell>
                <TableCell align="right" data-label="Produtos">{c.qtdProdutos}</TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Editar">
                    <IconButton color="primary" onClick={() => abrirForm(c.id)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton color="error" onClick={() => setConfirmId(c.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>
      {/* paginação */}
      <Stack spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={(_e, p) => setPagina(p)} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {items.length} de {totalItens} campanhas (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <EntityFormDialog
        open={form.open}
        title={form.id ? 'Editar campanha' : 'Nova campanha'}
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="xl"
        fullHeight
      >
        <FormCampaign
          key={form.id || 'novo'}
          embedded
          campaignId={form.id}
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      {/* diálogo */}
      <Dialog open={!!confirmId} onClose={() => setConfirmId(null)}>
        <DialogTitle>Excluir Campanha</DialogTitle>
        <DialogContent>
          <DialogContentText>Confirmar exclusão?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)}>Cancelar</Button>
          <Button color="error" onClick={excluir}>Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* snackbar */}
      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)} anchorOrigin={{vertical:'bottom', horizontal:'right'}}>
        <Alert severity={snackSeverity} onClose={() => setSnackOpen(false)}>{snackMsg}</Alert>
      </Snackbar>
    </Box>
  );
}
